import type { ColumnDropPosition } from '@/components/workspace/useColumnOrder';

export type ColumnHeaderGeometry = {
  id: string;
  left: number;
  right: number;
  center: number;
};

export type ColumnInsertTarget = {
  targetId: string;
  position: ColumnDropPosition;
  insertIndex: number;
};

/**
 * Resolve the insertion gap from pointer X.
 * Always canonicalize a gap as "before" the column on its right (or "after" the last column).
 * That prevents adjacent headers from fighting over the same gap (after A vs before B).
 */
export function resolveColumnInsertTarget(
  clientX: number,
  headers: ColumnHeaderGeometry[],
  draggedId: string,
  previousInsertIndex: number | null = null
): ColumnInsertTarget | null {
  const others = headers.filter((h) => h.id && h.id !== draggedId);
  if (others.length === 0) return null;

  // Dead-zone around the previous boundary so tiny pointer jitter doesn't flip gaps.
  if (previousInsertIndex != null) {
    const prev = Math.max(0, Math.min(previousInsertIndex, others.length));
    const boundaryX =
      prev >= others.length ? others[others.length - 1].right : others[prev].left;
    const deadZone = 12;
    if (Math.abs(clientX - boundaryX) < deadZone) {
      return insertIndexToTarget(others, prev);
    }
  }

  let insertIndex = others.length;
  for (let i = 0; i < others.length; i++) {
    if (clientX < others[i].center) {
      insertIndex = i;
      break;
    }
  }

  return insertIndexToTarget(others, insertIndex);
}

function insertIndexToTarget(
  others: ColumnHeaderGeometry[],
  insertIndex: number
): ColumnInsertTarget {
  if (insertIndex >= others.length) {
    const last = others[others.length - 1];
    return { targetId: last.id, position: 'after', insertIndex: others.length };
  }
  return {
    targetId: others[insertIndex].id,
    position: 'before',
    insertIndex,
  };
}

export function readMovableHeaderGeometry(row: Element): ColumnHeaderGeometry[] {
  const nodes = row.querySelectorAll<HTMLElement>(
    'th[data-workspace-col-id][data-workspace-movable]'
  );
  const out: ColumnHeaderGeometry[] = [];
  nodes.forEach((node) => {
    const id = node.dataset.workspaceColId;
    if (!id) return;
    const rect = node.getBoundingClientRect();
    out.push({
      id,
      left: rect.left,
      right: rect.right,
      center: rect.left + rect.width / 2,
    });
  });
  return out;
}
